import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Settings as Gear, Flame, Crown, BookOpen, Clapperboard, Compass, Mic, Clock, GraduationCap, Pencil, Camera, Trash2, Check } from "lucide-react";
import AppShell from "@/components/AppShell";
import CefrBadge from "@/components/CefrBadge";
import AchievementIcon from "@/components/AchievementIcon";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/3085e34f-2862-4361-89ba-1e9784a7f039/images/0288d6d54a29db1346e8ba36aa90923e7b07ed147f1ff5dbfe1ff40cde6d9b06.jpeg";

export default function Profile() {
  const nav = useNavigate();
  const { user, premium, refresh } = useAuth();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [prog, setProg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [avatars, setAvatars] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    const load = () => {
      client.get("/learn/passport").then((r) => setData(r.data)).catch(() => setData({ passports: [], achievements: [], history: [] }));
      client.get("/learn/progress").then((r) => setProg(r.data)).catch(() => setProg(null));
    };
    load();
    const on = () => load();
    window.addEventListener("mel:progress-refresh", on);
    return () => window.removeEventListener("mel:progress-refresh", on);
  }, []);
  useEffect(() => { setName(user?.name || ""); }, [user?.name]);

  const openEdit = () => {
    setEditing(true);
    if (avatars.length === 0) client.get("/profile/avatars").then((r) => setAvatars(r.data || [])).catch(() => {});
  };
  const saveName = async () => {
    setBusy(true);
    try { await client.put("/profile", { name: name.trim() || "Learner" }); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await client.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refresh(); toast.success("Photo updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const pickPreset = async (url) => {
    setBusy(true);
    try { await client.post("/profile/avatar-preset", { url }); await refresh(); toast.success("Avatar updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const removePhoto = async () => {
    setBusy(true);
    try { await client.delete("/profile/avatar"); await refresh(); toast.success("Photo removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const initial = (user?.name || user?.email || "U")[0].toUpperCase();
  const active = data?.passports?.[0];
  const avatar = user?.avatar_url;
  const fmtTime = (min) => { const m = Math.round(min || 0); return m >= 60 ? `${Math.round(m / 60)}h` : `${m}m`; };
  const stats = [
    { label: t("prof_stat_lessons"), val: active?.lessons_completed || 0, icon: GraduationCap, tint: "#2563EB" },
    { label: t("prof_stat_words"), val: active?.words || 0, icon: BookOpen, tint: "#3B82F6" },
    { label: t("prof_stat_learning"), val: fmtTime(active?.learning_minutes), icon: Clock, tint: "#8B5CF6" },
    { label: t("prof_stat_speaking"), val: fmtTime(active?.speaking_minutes), icon: Mic, tint: "#2563EB" },
    { label: t("prof_stat_stories"), val: prog?.stories ?? 0, icon: Clapperboard, tint: "#3B82F6" },
    { label: t("prof_stat_life"), val: prog?.life_missions ?? prog?.life ?? 0, icon: Compass, tint: "#22C55E" },
  ];

  return (
    <AppShell pad={false} nav={!editing}>
      {/* Hero banner */}
      <div className="relative">
        <img src={HERO} alt="" className="w-full h-52 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-[#F8FAFC]" />
        <button onClick={() => nav("/settings")} data-testid="settings-gear"
          className="absolute top-11 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow"><Gear size={19} className="text-[#0F172A]" /></button>
        <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
          {avatar
            ? <img src={avatar} alt="" data-testid="passport-avatar" className="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-lg" />
            : <div data-testid="passport-avatar" className="w-24 h-24 rounded-full ring-4 ring-white shadow-lg bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center font-heading text-4xl font-extrabold text-white">{initial}</div>}
          <button onClick={openEdit} data-testid="edit-avatar-btn" className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#2563EB] ring-2 ring-white flex items-center justify-center shadow"><Camera size={15} className="text-white" /></button>
        </div>
      </div>

      <div className="px-6 pt-12 pb-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{user?.name || "Learner"}</h1>
          {premium && <span className="text-[11px] font-extrabold rounded-full px-2 py-0.5 bg-[#2563EB] text-white">PRO</span>}
        </div>
        {active && <p className="text-[#475569] mt-1 flex items-center justify-center gap-2"><span className="text-lg">{active.flag}</span>{active.name} · <CefrBadge level={active.level} size="sm" /></p>}
        <button onClick={openEdit} data-testid="edit-profile-btn" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] bg-[#2563EB]/8 rounded-full px-4 py-1.5"><Pencil size={14} /> {t("prof_edit")}</button>
      </div>

      {!data ? <div className="pt-6 flex justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div> : (
        <div className="px-5 pb-6">
          {/* Streaks */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="rounded-2xl bg-white border border-slate-200/70 p-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-[#FEF3C7] flex items-center justify-center"><Flame size={18} className="text-[#F59E0B]" /></span>
              <div><p className="text-[11px] text-[#475569] font-semibold">{t("prof_current_streak")}</p><p className="font-heading font-extrabold text-[#0F172A]">{data.streak || 0} {t("prof_days")}</p></div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200/70 p-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-[#FEF9C3] flex items-center justify-center"><Crown size={18} className="text-[#CA8A04]" /></span>
              <div><p className="text-[11px] text-[#475569] font-semibold">{t("prof_best_streak")}</p><p className="font-heading font-extrabold text-[#0F172A]">{prog?.best_streak ?? data.streak ?? 0} {t("prof_days")}</p></div>
            </div>
          </div>

          {/* Achievements */}
          <div className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t("prof_achievements")}</p>
            <div className="grid grid-cols-4 gap-2">
              {data.achievements.map((a) => (
                <div key={a.code} data-testid={`ach-${a.code}`} className={`rounded-2xl p-3 text-center border ${a.unlocked ? "bg-[#E0F2FE] border-[#2563EB]/20" : "bg-white border-slate-200 opacity-50"}`}>
                  <AchievementIcon code={a.code} size={20} className={`mx-auto ${a.unlocked ? "text-[#2563EB]" : "text-slate-300"}`} />
                  <p className="text-[10px] font-semibold text-[#0F172A] mt-1 leading-tight">{t(`ach_${a.code}`)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recent activity */}
          {data.history?.length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t("prof_recent_activity")}</p>
              <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
                {data.history.slice(0, 6).map((h, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                    <span className="text-slate-600">{new Date(h.date).toLocaleDateString(lang === "de" ? "de-DE" : "en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                    <span className="text-slate-400">{h.lessons ? `${t(h.lessons > 1 ? "act_lessons" : "act_lesson", { n: h.lessons })} · ` : ""}{Math.round(h.minutes)} {t("common_min")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="avatar-file-input" />
      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end justify-center" onClick={() => setEditing(false)}>
          <div className="w-full max-w-[460px] bg-white rounded-t-3xl p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-profile-modal" style={{ maxHeight: "88dvh", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 28px)" }}>
            <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-4" />
            <p className="font-heading text-lg font-extrabold text-[#0F172A] mb-3">{t("prof_edit")}</p>

            <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("prof_display_name")}</label>
            <div className="flex gap-2 mt-1.5 mb-5">
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name-input"
                className="flex-1 h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[#0F172A] outline-none focus:border-[#2563EB]" />
              <button onClick={saveName} disabled={busy} data-testid="edit-name-save" className="px-4 rounded-xl bg-[#2563EB] text-white font-bold disabled:opacity-60">Save</button>
            </div>

            <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("prof_photo")}</label>
            <div className="flex gap-2 mt-1.5 mb-5">
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-photo-btn"
                className="flex-1 h-12 rounded-xl bg-slate-100 text-[#0F172A] font-semibold flex items-center justify-center gap-2 disabled:opacity-60"><Camera size={17} /> {t("prof_upload_photo")}</button>
              {avatar && <button onClick={removePhoto} disabled={busy} data-testid="remove-photo-btn" className="w-12 h-12 rounded-xl bg-red-50 text-red-500 flex items-center justify-center disabled:opacity-60"><Trash2 size={18} /></button>}
            </div>

            <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("prof_avatars")}</label>
            <div className="grid grid-cols-4 gap-3 mt-2">
              {avatars.map((a) => (
                <button key={a.id} onClick={() => pickPreset(a.url)} disabled={busy} data-testid={`avatar-${a.id}`}
                  className="relative rounded-full overflow-hidden aspect-square ring-2 ring-transparent focus:ring-[#2563EB] disabled:opacity-60">
                  <img src={a.url} alt="" className="w-full h-full object-cover" />
                  {avatar === a.url && <span className="absolute inset-0 bg-[#2563EB]/30 flex items-center justify-center"><Check size={20} className="text-white" /></span>}
                </button>
              ))}
            </div>
            {busy && <div className="flex justify-center mt-4"><Loader2 className="animate-spin text-[#2563EB]" /></div>}
          </div>
        </div>
      )}

    </AppShell>
  );
}
